import { useCallback, useEffect, useState } from "react";
import { Gavel, Inbox, Loader2, Send, ShieldCheck, ThumbsDown, Wallet } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Kpi, Loading, PageHeader, Panel } from "@/components/common/Chrome";
import { ApprovalRow, SlabTable } from "@/components/common/Approvals";
import Exceptions from "@/pages/Exceptions";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";

export default function Approvals() {
  const { can } = useAuth();
  const [state, setState] = useState(null);
  const [digest, setDigest] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const [res, d] = await Promise.all([api.get("/approvals"), api.get("/approvals/digest/status")]);
    setState(res.data);
    setDigest(d.data);
  }, []);

  const sendDigest = async () => {
    setSending(true);
    try {
      const res = await api.post("/approvals/digest/run");
      toast.success(res.data.recipients
        ? `Digest sent to ${res.data.recipients} approver(s)`
        : res.data.already_sent
          ? "Today's digest has already gone out to everyone waiting"
          : "Nothing is waiting on anyone right now");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setSending(false);
  };

  useEffect(() => { load().catch(() => setState(false)); }, [load]);

  if (!state) return <Loading label="Loading the approval inbox" />;
  const { items, counters, slabs } = state;
  const groups = {
    yours: items.filter((i) => i.status === "pending" && i.you_can_vote),
    pending: items.filter((i) => i.status === "pending"),
    decided: items.filter((i) => i.status !== "pending"),
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-031 · Delegated authority"
        title="Every rupee above your slab needs another signature"
        description="Budgets, expenses, payments and conflicted decisions are routed by value: the Treasurer alone at the first slab, the Treasurer with the Board Secretary at the second, both with the President or Chairperson at the third, and a majority vote of the sitting trustees above that. Approvals and declines are both counted, and voting can be made anonymous for the whole tenant."
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {can("approval", "vote") && (
          <Kpi testId="kpi-awaiting-you" label="Awaiting your decision" value={counters.awaiting_you}
               accent="amber" icon={Inbox} />
        )}
        <Kpi testId="kpi-pending" label="Open requests" value={counters.pending} accent="sky"
             icon={Gavel} />
        <Kpi testId="kpi-value-pending" label="Value held" accent="violet" icon={Wallet}
             value={money(counters.value_pending, "INR", true)} />
        <Kpi testId="kpi-approved" label="Approved" value={counters.approved} accent="emerald"
             icon={ShieldCheck} />
        <Kpi testId="kpi-declined" label="Declined" value={counters.declined} accent="red"
             icon={ThumbsDown} />
      </div>

      <Panel testId="panel-slabs" kicker="Tenant policy" title="Authority slabs in force">
        <div className="space-y-3 p-4 pt-0">
          <SlabTable slabs={slabs} />
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-muted/30 p-3">
            <div className="text-[11.5px] text-muted-foreground">
              A digest goes out every morning at 07:30 to everyone with something waiting on their
              signature — in the portal, by email and on WhatsApp, according to each person's own
              channel choices.{digest?.your_last_digest?.day
                ? ` Your last digest went out on ${digest.your_last_digest.day} covering ${digest.your_last_digest.count} item(s).`
                : " You have not been sent one yet."}
            </div>
            {can("approval", "write") && (
              <Button size="sm" variant="outline" data-testid="send-digest-button" disabled={sending}
                      onClick={sendDigest}
                      className="h-8 shrink-0 rounded-full border-border text-[11px]">
                {sending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  : <Send className="mr-1 h-3.5 w-3.5" />} Send today's digest now
              </Button>
            )}
          </div>
        </div>
      </Panel>

      <Tabs defaultValue="yours">
        <TabsList>
          <TabsTrigger value="yours" data-testid="tab-yours">Needs you ({groups.yours.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">All open ({groups.pending.length})</TabsTrigger>
          <TabsTrigger value="decided" data-testid="tab-decided">Decided ({groups.decided.length})</TabsTrigger>
          <TabsTrigger value="exceptions" data-testid="tab-exceptions">Exception requests</TabsTrigger>
        </TabsList>

        <TabsContent value="exceptions" className="mt-4">
          <Exceptions embedded />
        </TabsContent>
        {["yours", "pending", "decided"].map((key) => (
          <TabsContent key={key} value={key} className="mt-4 space-y-3">
            {groups[key].length === 0 && (
              <div className="panel p-8 text-center text-xs text-muted-foreground"
                   data-testid={`empty-${key}`}>
                Nothing here right now.
              </div>
            )}
            {groups[key].map((item) => (
              <ApprovalRow key={item.request_ref} item={item} onDone={load}
                           testId={`approval-${item.request_ref}`} />
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
